// Draw order for things standing in a room. Each thing is a box on the floor grid ([x0,x1] by [y0,y1]).
// A single "far corner" number per thing isn't enough once pieces span several tiles: a 1x3 sofa's far
// corner is nearer than someone standing just beside its back end, so the sofa used to cover them.
// Instead, anything wholly behind another on x or on y is drawn first, and the rest falls back to the key.
import type { Furni } from "../shared/protocol";

export type Drawable = { x0: number; y0: number; x1: number; y1: number; key: number; draw: () => void };

/** Floor box of a piece. Wall pieces are a sliver on their wall, so everyone in the room is in front. */
export const furniBox = (f: Furni) =>
  f.wall === 0 ? { x0: f.x, y0: 0, x1: f.x + f.w, y1: 0 }
  : f.wall === 1 ? { x0: 0, y0: f.y, x1: 0, y1: f.y + f.h }
  : { x0: f.x, y0: f.y, x1: f.x + f.w, y1: f.y + f.h };

/** Floor box of someone standing at (gx, gy) (fractional while walking): their feet, not the whole tile. */
export const personBox = (gx: number, gy: number) => ({ x0: gx + 0.25, y0: gy + 0.25, x1: gx + 0.75, y1: gy + 0.75 });

const behind = (a: Drawable, b: Drawable) => a.x1 <= b.x0 || a.y1 <= b.y0;

/** Draws everything back to front. */
export function drawInDepthOrder(list: Drawable[]) {
  const n = list.length;
  const after: number[][] = list.map(() => []);
  const waits = new Array<number>(n).fill(0);
  for (let i = 0; i < n; i++)
    for (let j = i + 1; j < n; j++) {
      const a = list[i], b = list[j];
      const ab = behind(a, b), ba = behind(b, a);
      // side by side (both true) never overlap on screen; overlapping footprints use the old key
      const first = ab && !ba ? i : ba && !ab ? j : ab && ba ? -1 : a.key <= b.key ? i : j;
      if (first < 0) continue;
      const second = first === i ? j : i;
      after[first].push(second);
      waits[second]++;
    }
  const done = new Array<boolean>(n).fill(false);
  for (let k = 0; k < n; k++) {
    // nearest-to-the-back of the ready ones; if a loop ever leaves none ready, the lowest key goes anyway
    let pick = -1;
    for (let i = 0; i < n; i++) if (!done[i] && !waits[i] && (pick < 0 || list[i].key < list[pick].key)) pick = i;
    if (pick < 0) for (let i = 0; i < n; i++) if (!done[i] && (pick < 0 || list[i].key < list[pick].key)) pick = i;
    done[pick] = true;
    for (const m of after[pick]) waits[m]--;
    list[pick].draw();
  }
}
